import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { FileSearch, FolderOpen, Hash, Home, Info, RefreshCw, Share2, ShieldCheck } from "lucide-react";

import CollapsibleSection from "js/components/Tenants/TenantReportDetails/CollapsibleSection";
import { Card } from "js/components/ui/card";
import { Button } from "js/components/ui/button";
import DownloadsDetailsComponent from "js/components/Tenants/TenantReportDetails/DownloadsDetailsComponent";
import { getStatusInfo } from "js/components/Tenants/TenantReportDetails/HighlightsPanel";
import LeaseContractDetailsComponent from "js/components/Tenants/TenantReportDetails/LeaseContractDetailsComponent";
import { ReportDetailSkeleton } from "js/components/Tenants/TenantReportDetails/ReportDetailSkeleton";
import RequirementDetailsMultiexpedienteComponent from "js/components/Tenants/TenantReportDetails/RequirementDetailsMultiexpedienteComponent";
import ShareReportModal from "js/components/Tenants/TenantReportDetails/ShareReportModal";
import PeopleReportSection from "js/components/Tenants/TenantReportDetails/PeopleReportSection";
import GuarantorInvestigationUpsell from "js/components/Tenants/TenantReportDetails/GuarantorInvestigationUpsell";
import {
  buildDownloadItems,
  isRequirementValid,
} from "js/components/Tenants/TenantReportDetails/downloads";
import {
  GuarantorDetails,
  LandlordDetails,
  LeaseContractDetails,
  PropertyDetails,
  TenantDetails,
} from "js/components/Tenants/TenantReportDetails/typing";
import {
  StatusBadge,
  statusBadgeVariants,
} from "js/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

import { useIsDesktop } from "../../../hooks/useMediaQuery";
import { useGetTenantReportDetails } from "../../../hooks/useTenantReports";
import { toast } from "utils/toast";

// ---------------------------------------------------------------------------
// Error boundary fallback
// ---------------------------------------------------------------------------

function SectionErrorFallback() {
  return (
    <div className="flex items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5 p-6">
      <p className="text-sm text-destructive">
        Ocurrió un error al cargar esta sección.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TenantReportDetails = () => {
  const { reportId } = useParams();
  const {
    data: reportData,
    isLoading,
    refetch,
    isFetching,
  } = useGetTenantReportDetails(reportId!);

  const handleRefresh = async () => {
    const result = await refetch();
    if (result.isError) {
      toast.error("No se pudo actualizar la Solicitud.");
    } else {
      toast.success("Solicitud actualizada.");
    }
  };
  const [multiExpFolderNumber, setMultiExpFolderNumber] = useState<
    string | null
  >(null);
  const [shareReportModalIsOpen, setShareReportModalIsOpen] =
    useState<boolean>(false);
  const [guarantorData, setGuarantorData] = useState<GuarantorDetails | null>(
    null,
  );
  const [tenantData, setTenantData] = useState<TenantDetails | null>(null);
  const [landlordData, setLandlordData] = useState<LandlordDetails | null>(
    null,
  );
  const [propertyData, setPropertyData] = useState<PropertyDetails | null>(
    null,
  );
  const [leaseContractData, setLeaseContractData] =
    useState<LeaseContractDetails | null>(null);
  const [downloadsData, setDownloadsData] = useState<any[] | null>(null);

  const isDesktop = useIsDesktop();

  const validateForm = (subfolder: string, slug: string) =>
    isRequirementValid(reportData, subfolder, slug);
  const isGuarantorValid = (type: string, slug: string) =>
    reportData?.required_documents?.[type] && validateForm(type, slug);

  // Helper function to safely get validated data from multiexpediente_validated_data
  const getValidatedData = (entity: string) => {
    const validatedData = reportData?.multiexpediente_validated_data;

    // Check if multiexpediente_validated_data exists and is not empty
    if (
      !validatedData ||
      typeof validatedData !== "object" ||
      Object.keys(validatedData).length === 0
    ) {
      return null;
    }

    // Check if entity exists in validated data
    const entityData = validatedData[entity];
    if (!entityData || typeof entityData !== "object") {
      return null;
    }

    // Check if entity is validated
    if (entityData.is_validated !== true) {
      return null;
    }

    // Return fields if they exist
    return entityData.fields || null;
  };

  // Initialize multiExpFolderNumber when reportData loads
  useEffect(() => {
    if (reportData) {
      setMultiExpFolderNumber(reportData.multiexpediente_folio);
    }
  }, [reportData]);

  // slug -> { valid, total } for the "X de Y documentos validados" footer in each person.
  const docCounts = useMemo(() => {
    const rd = reportData?.required_documents ?? {};
    const out: Record<string, { valid: number; total: number }> = {};
    for (const [slug, reqs] of Object.entries(rd)) {
      const list = (reqs as any[]) ?? [];
      out[slug] = {
        valid: list.filter((r) => r?.status === "valid").length,
        total: list.length,
      };
    }
    return out;
  }, [reportData?.required_documents]);

  const expedienteHasDocuments = useMemo(() => {
    const groups = reportData?.required_documents;
    if (!groups) return false;
    return Object.values(groups).some((requirements) =>
      requirements?.some((req) => (req?.files?.length ?? 0) > 0),
    );
  }, [reportData?.required_documents]);

  useEffect(() => {
    const getPropertyData = (): PropertyDetails => {
      const propertyEntity =
        reportData?.multiexpediente_validated_data?.property;
      // Fields are surfaced progressively by the backend (even before the whole template
      // is validated). "Verificado" only turns on when the full lease-contract template is.
      const fields = propertyEntity?.fields;
      const isPropertyValidated = propertyEntity?.is_validated === true;

      const validatedContract = getValidatedData("lease_contract");
      const vd = reportData?.folder_data?.validity_and_dates_data;
      const reportAddr = reportData?.property?.address;

      // First non-empty value (progressive: multiexpediente fields → fallback data).
      const pick = (...vals: any[]) =>
        vals.find((v) => v !== null && v !== undefined && v !== "");

      const src: any = fields ?? reportData?.folder_data?.property_data ?? {};

      const address = {
        street: pick(src.street, reportAddr?.street) ?? "",
        exterior_number:
          pick(src.exterior_number, reportAddr?.exterior_number) ?? "",
        interior_number:
          pick(src.interior_number, reportAddr?.interior_number) ?? null,
        zip_code: pick(src.zip_code, reportAddr?.zip_code) ?? "",
        neighborhood: pick(src.neighborhood, reportAddr?.neighborhood) ?? "",
        city: pick(src.city, reportAddr?.city) ?? "",
        state: pick(src.state, reportAddr?.state) ?? "",
      };

      const rental = pick(
        src.rent_amount,
        validatedContract?.rent_amount,
        reportData?.lease_contract?.rental_and_deposit?.monthly_rent,
        vd?.rent_amount,
        reportData?.rental_price,
        reportData?.property?.rental_price,
      );
      const maintenance = pick(
        src.maintenance_amount,
        validatedContract?.maintenance_amount,
        vd?.maintenance_amount,
        reportData?.maintenance_price,
        reportData?.property?.maintenance_price,
      );

      return {
        id: reportData?.property?.id,
        verified: isPropertyValidated,
        address,
        alias: pick(src.alias, reportData?.property?.alias) ?? "",
        usage: pick(src.usage, reportData?.property_usage) ?? undefined,
        maintenance_price: maintenance != null ? Number(maintenance) : 0,
        rental_price: rental != null ? Number(rental) : 0,
      };
    };

    setPropertyData(getPropertyData());
  }, [
    reportData?.required_documents,
    reportData?.lease_contract,
    reportData?.property,
  ]);

  useEffect(() => {
    const getLandlordData = (): LandlordDetails | null => {
      // Check if landlord exists at all
      if (!reportData?.landlord && !reportData?.folder_data?.landlord_data) {
        // Check if validated landlord exists
        const validatedLandlord = getValidatedData("landlord");
        if (!validatedLandlord) {
          return null;
        }
      }

      // 1st Priority: Try to get validated data from multiexpediente_validated_data
      const validatedLandlord = getValidatedData("landlord");
      if (validatedLandlord) {
        const isNatural =
          reportData?.multiexpediente_validated_data?.landlord?.is_natural ??
          true;

        return {
          verified: true,
          name: validatedLandlord.name || "",
          first_surname: validatedLandlord.first_surname || "",
          second_surname: validatedLandlord.second_surname || "",
          email: validatedLandlord.email || "",
          phone_number: validatedLandlord.phone || "",
          birth_date: validatedLandlord.birth_date || "",
          rfc: validatedLandlord.rfc || "",
          curp: validatedLandlord.curp || "",
          legal_type: isNatural ? 1 : 2,
        };
      }

      // 2nd Priority: Fallback to existing logic
      const landlordData = reportData?.folder_data?.landlord_data;

      if (landlordData) {
        const isNatural = landlordData.is_natural === 1;
        const contractType = isNatural
          ? "lease-contract-pf"
          : "lease-contract-pm";
        const formType = isNatural ? "landlord-form" : "legal-rep-short-form";

        if (validateForm(contractType, formType)) {
          const {
            first_name = "",
            second_name = "",
            first_last_name = "",
            second_last_name = "",
            email = "",
            phone = "",
            birth_date = "",
            rfc = "",
            curp = "",
          } = landlordData;

          return {
            verified: true,
            name: `${first_name} ${second_name}`.trim(),
            first_surname: first_last_name,
            second_surname: second_last_name,
            email,
            phone_number: phone,
            birth_date,
            rfc,
            curp,
            legal_type: isNatural ? 1 : 2,
          };
        }
      }

      return reportData?.landlord ?? null;
    };
    setLandlordData(getLandlordData());
  }, [reportData]);

  useEffect(() => {
    const getTenantData = (): TenantDetails => {
      // 1st Priority: Try to get validated data from multiexpediente_validated_data
      const validatedPerson = getValidatedData("person");
      if (validatedPerson) {
        const isNatural =
          reportData?.multiexpediente_validated_data?.person?.is_natural ??
          true;

        return {
          verified: true,
          name: validatedPerson.name || "",
          first_surname: validatedPerson.first_surname || "",
          second_surname: validatedPerson.second_surname || "",
          email: validatedPerson.email || "",
          phone_number: validatedPerson.phone || "",
          birth_date: validatedPerson.birth_date || null,
          rfc: validatedPerson.rfc || "",
          curp: validatedPerson.curp || "",
          legal_type: isNatural ? "Natural" : "Moral",
        };
      }

      // 2nd Priority: Fallback to existing logic.
      // The report's own `legal_type` comes first on purpose: it is the non-nullable column
      // the backend uses to name the expediente subfolders (tenant-pf / tenant-pm), while
      // `person` is a nullable FK that some reports simply don't have. Reading the legal
      // type off `person` there meant a person-less report silently looked for the
      // persona-moral folder and lost its "Verificado" badge and validated folder data.
      const person = reportData?.person;
      const legalType = reportData?.legal_type ?? person?.legal_type;
      const isNatural = legalType === "Natural";

      const subfolder = isNatural ? "tenant-pf" : "tenant-pm";
      const formType = isNatural ? "tenant-form" : "legal-rep-form";

      if (validateForm(subfolder, formType)) {
        const tenantData = reportData?.folder_data?.applicant_data ?? {};

        const {
          first_name = "",
          second_name = "",
          first_surname = "",
          second_surname = "",
          email = "",
          mobile_phone = "",
          birth_date = null,
          rfc = "",
          curp = "",
        } = tenantData;

        return {
          verified: true,
          name: `${first_name} ${second_name}`.trim(),
          first_surname,
          second_surname,
          email,
          phone_number: mobile_phone,
          birth_date,
          rfc,
          curp,
          legal_type: legalType,
        };
      }

      return {
        ...person,
        legal_type: legalType,
        birth_date: person?.birth_date
          ? dayjs(person.birth_date).format("DD/MM/YYYY")
          : null,
      };
    };

    setTenantData(getTenantData());
  }, [reportData]);

  useEffect(() => {
    const getGuarantorData = (): GuarantorDetails | null => {
      // Check if guarantor exists at all
      if (!reportData?.guarantor && !reportData?.folder_data?.guarantor_data) {
        // Check if validated guarantor exists
        const validatedGuarantor = getValidatedData("guarantor");
        if (!validatedGuarantor) {
          return null;
        }
      }

      // 1st Priority: Try to get validated data from multiexpediente_validated_data
      const validatedGuarantor = getValidatedData("guarantor");
      if (validatedGuarantor) {
        const isNatural =
          reportData?.multiexpediente_validated_data?.guarantor?.is_natural ??
          true;

        return {
          verified: true,
          name: validatedGuarantor.name || "",
          first_surname: validatedGuarantor.first_surname || "",
          second_surname: validatedGuarantor.second_surname || "",
          email: validatedGuarantor.email || "",
          phone_number: validatedGuarantor.phone || "",
          birth_date: validatedGuarantor.birth_date || "",
          rfc: validatedGuarantor.rfc || "",
          curp: validatedGuarantor.curp || "",
          legal_type: isNatural ? 1 : 2,
        };
      }

      // 2nd Priority: Fallback to existing logic
      const hasInvestigationDocs =
        isGuarantorValid("guarantor-investigation-pf", "guarantor-form") ||
        isGuarantorValid("guarantor-investigation-pm", "legal-rep-short-form");

      const hasGuarantorDocs =
        isGuarantorValid("guarantor-pf", "guarantor-form") ||
        isGuarantorValid("guarantor-pm", "legal-rep-short-form");

      if (hasInvestigationDocs || hasGuarantorDocs) {
        const guarantor_data = reportData?.folder_data?.guarantor_data;
        if (!guarantor_data) return reportData?.guarantor ?? null;

        const {
          name,
          second_name,
          first_surname,
          second_surname,
          email,
          mobile_phone,
          is_natural,
          birth_date,
          rfc = "",
          curp = "",
        } = guarantor_data;

        return {
          verified: true,
          name: `${name} ${second_name}`.trim(),
          first_surname,
          second_surname,
          email,
          phone_number: mobile_phone,
          legal_type: is_natural ? 1 : 2,
          birth_date,
          rfc,
          curp,
        };
      }

      return reportData?.guarantor ?? null;
    };
    setGuarantorData(getGuarantorData());
  }, [reportData]);

  useEffect(() => {
    const getContractData = () => {
      // The generated Contract model is the source of truth for the contract's terms and
      // lifecycle. Prefer it per-field; fall back to the multiexpediente validated data /
      // folder data for anything it doesn't carry (or when there's no contract yet).
      const contract = reportData?.lease_contract;
      const validated = getValidatedData("lease_contract");
      const vd = reportData?.folder_data?.validity_and_dates_data;
      const leaseContractSlugs = ["lease-contract-pf", "lease-contract-pm"];
      const hasRequiredDocs = leaseContractSlugs.some(
        (slug) => reportData?.required_documents?.[slug],
      );

      if (!contract && !validated && !(hasRequiredDocs && vd)) return null;

      const pick = (...vals: any[]) =>
        vals.find((v) => v != null && v !== "") ?? null;
      const toInt = (v: any) =>
        v == null || v === "" ? null : parseInt(String(v), 10);
      const toFloat = (v: any) =>
        v == null || v === "" ? null : parseFloat(String(v));

      return {
        verified: !!(contract || validated),
        // Lifecycle comes only from the Contract model.
        status: contract?.status ?? null,
        signed_at:
          contract?.closed_at ?? reportData?.folder_data?.signed_at ?? null,
        // Terms: Contract → validated → folder data.
        start_date: pick(
          contract?.start_date,
          validated?.start_date,
          vd?.start_date,
        ),
        end_date: pick(contract?.end_date, validated?.end_date, vd?.end_date),
        payment_day: pick(
          contract?.validity_and_dates?.payment_day,
          toInt(validated?.payment_day),
          vd?.payment_day,
        ),
        grace_period: pick(
          contract?.validity_and_dates?.grace_period,
          toInt(validated?.grace_period),
          vd?.grace_period,
        ),
        payment_method: pick(
          contract?.validity_and_dates?.payment_method,
          validated?.payment_method,
          vd?.payment_method,
        ),
        guarantee_deposit: pick(
          contract?.rental_and_deposit?.guarantee_deposit,
          toFloat(validated?.deposit_amount),
          vd?.deposit_amount,
        ),
      };
    };

    setLeaseContractData(getContractData());
  }, [reportData]);

  useEffect(() => {
    setDownloadsData(buildDownloadItems(reportData));
  }, [reportData]);

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading || !reportData) {
    return <ReportDetailSkeleton />;
  }

  // The property ("Inmueble") is a part of the lease, so it renders inside "Partes del
  // Arrendamiento". Structure kept identical to the previous standalone card.
  const inmuebleContent = propertyData ? (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <div className="flex min-w-[240px] flex-1 items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Home className="size-5 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {[
              propertyData.address?.street,
              propertyData.address?.exterior_number,
            ]
              .filter(Boolean)
              .join(" ")}
            {propertyData.address?.interior_number
              ? `, Int. ${propertyData.address.interior_number}`
              : ""}
          </div>
          <div className="text-xs text-muted-foreground">
            {[
              propertyData.address?.neighborhood
                ? `Col. ${propertyData.address.neighborhood}`
                : "",
              propertyData.address?.city,
              propertyData.address?.zip_code,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <div>
          <div className="text-xs text-muted-foreground">Uso</div>
          <div className="text-sm font-semibold capitalize">
            {propertyData.usage || reportData?.property_usage || "—"}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Renta</div>
          <div className="text-sm font-semibold">
            {propertyData.rental_price != null
              ? `$${new Intl.NumberFormat("es-MX").format(propertyData.rental_price)} `
              : "— "}
            <span className="text-[11px] font-medium text-muted-foreground">
              MXN
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Mantenimiento</div>
          <div className="text-sm font-semibold">
            {propertyData.maintenance_price != null
              ? `$${new Intl.NumberFormat("es-MX").format(propertyData.maintenance_price)} `
              : "— "}
            <span className="text-[11px] font-medium text-muted-foreground">
              MXN
            </span>
          </div>
        </div>
        {propertyData.verified && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={`${statusBadgeVariants({ status: "success" })} cursor-help`}
              >
                <span className="size-1.5 shrink-0 rounded-full bg-emerald-500" />
                Verificado
                <Info className="size-3 shrink-0 opacity-70" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              Se marca cuando se validan los requisitos de identidad del
              propietario y la documentación que acredita la propiedad (predial
              o escrituras).
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  ) : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Report sections extracted so mobile can reorder them: desktop keeps the two
  // columns (Partes + Documentos | Investigación y contrato); mobile stacks them
  // Partes → Investigación y contrato → Documentos.
  const partesSection = (
    <Sentry.ErrorBoundary fallback={<SectionErrorFallback />}>
      <PeopleReportSection
        tenant={tenantData}
        guarantor={guarantorData}
        landlord={landlordData}
        additionalParticipants={reportData?.additional_participants}
        docCounts={docCounts}
        inmuebleSlot={inmuebleContent}
        inmuebleValidated={propertyData?.verified}
        guarantorSlot={
          guarantorData ? (
            <GuarantorInvestigationUpsell
              guarantor={guarantorData}
              folio={reportData?.multiexpediente_folio}
              reportId={reportId}
              reportStatus={reportData?.status}
              guarantorRequired={reportData?.guarantor_required}
              guarantorInvestigationRequired={
                reportData?.guarantor_investigation_required
              }
              guarantorUpgradeStatus={reportData?.guarantor_upgrade_status}
              paymentUrl={reportData?.payment_url}
              onGuarantorUpdate={setGuarantorData}
            />
          ) : undefined
        }
        guarantorChip={
          guarantorData &&
          reportData?.guarantor_required === true &&
          (reportData?.status === "In Progress" ||
            reportData?.status === "in_progress") &&
          ((reportData?.guarantor_investigation_required === false &&
            reportData?.guarantor_upgrade_status == null) ||
            reportData?.guarantor_upgrade_status === "Pending Payment") ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
              <ShieldCheck className="size-3.5" />
              Verificación disponible
            </span>
          ) : undefined
        }
      />
    </Sentry.ErrorBoundary>
  );

  const documentosSection = (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: 0.2 }}
    >
      <Sentry.ErrorBoundary fallback={<SectionErrorFallback />}>
        <CollapsibleSection
          title="Documentos del Expediente"
          icon={FolderOpen}
          defaultOpen
        >
          <RequirementDetailsMultiexpedienteComponent
            reportUrl={reportData?.multiexpediente_url}
            requirements={reportData?.required_documents}
            tenantReportId={reportId!}
            multiexpedienteFolderNumber={multiExpFolderNumber}
            expedienteHasDocuments={expedienteHasDocuments}
          />
        </CollapsibleSection>
      </Sentry.ErrorBoundary>
    </motion.div>
  );

  const investigacionSection = (
    <Sentry.ErrorBoundary fallback={<SectionErrorFallback />}>
      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex items-center gap-2.5 px-4 pb-3 pt-4">
          <FileSearch className="size-4.5 text-muted-foreground" />
          <h3 className="text-base font-semibold leading-none">
            Investigación y contrato
          </h3>
        </div>
        <div className="h-px bg-border" />
        <LeaseContractDetailsComponent
          asSubBlock
          folio={reportData?.multiexpediente_folio}
          // A lease-contract-* subfolder in the expediente means the contract was already
          // requested — don't recommend requesting it again, even before its data is filled.
          contractRequested={["lease-contract-pf", "lease-contract-pm"].some(
            (slug) => !!reportData?.required_documents?.[slug],
          )}
          lease_contract={leaseContractData ?? ({} as LeaseContractDetails)}
        />
        {downloadsData && (
          <DownloadsDetailsComponent
            asSubBlock
            tenant_report_id={reportData.id}
            downloadable={downloadsData}
            tenantPhoneNumber={
              tenantData?.phone_number
                ? `${tenantData?.dial_code ?? ""}${tenantData?.phone_number}`
                : ""
            }
            landlordPhoneNumber={
              landlordData?.phone_number
                ? `${landlordData?.dial_code ?? ""}${landlordData?.phone_number}`
                : ""
            }
          />
        )}
      </Card>
    </Sentry.ErrorBoundary>
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-semibold tracking-tight">
              Solicitud de Renta
            </h2>
            {reportData?.multiexpediente_folio && (
              <span className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-700">
                <Hash className="size-3.5 text-slate-500" />
                Folio {reportData.multiexpediente_folio}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Fase:</span>
            <StatusBadge
              status={getStatusInfo(reportData?.status).variant}
              label={getStatusInfo(reportData?.status).label}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
            aria-label="Actualizar"
          >
            <RefreshCw
              className={`size-4 ${isFetching ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareReportModalIsOpen(true)}
            aria-label="Compartir"
          >
            <Share2 className="size-4" />
            <span className="hidden sm:inline">Compartir</span>
          </Button>
        </div>
      </div>

      {/* Sections layout — desktop: two columns (Partes + Documentos | Investigación
          y contrato); mobile: Partes → Investigación y contrato → Documentos. */}
      {isDesktop ? (
        <div className="flex items-start gap-6">
          <div className="flex-1 min-w-0 space-y-4">
            {partesSection}
            {documentosSection}
          </div>
          <div className="w-[22rem] shrink-0 sticky top-6 space-y-4">
            {investigacionSection}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {partesSection}
          {investigacionSection}
          {documentosSection}
        </div>
      )}

      {/* Share modal */}
      <ShareReportModal
        open={shareReportModalIsOpen}
        setShareReportModalIsOpen={setShareReportModalIsOpen}
        reportUrl={reportData?.report_url}
        multiexpedienteUrl={reportData?.multiexpediente_url}
        tenantPhoneNumber={
          tenantData?.phone_number
            ? `${tenantData?.dial_code ?? ""}${tenantData?.phone_number}`
            : ""
        }
        landlordPhoneNumber={
          landlordData?.phone_number
            ? `${landlordData?.dial_code ?? ""}${landlordData?.phone_number}`
            : ""
        }
        guarantorPhoneNumber={
          guarantorData?.phone_number
            ? `${guarantorData?.dial_code ?? ""}${guarantorData?.phone_number}`
            : ""
        }
      />
    </div>
  );
};

export default TenantReportDetails;
